import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';

/**
 * @typedef {Object} InventoryVariantInfo
 * @property {boolean} managed
 * @property {number} quantity
 * @property {string} policy
 */

/**
 * @typedef {Object} InventoryConfig
 * @property {number} threshold
 * @property {boolean} showQuantity
 * @property {{ inStock: string, outOfStock: string, lowStock: string, lowStockShowCount: string }} labels
 * @property {Record<string, InventoryVariantInfo>} variants
 */

class ProductInventory extends Component {
  /** @type {InventoryConfig | null} */
  #config = null;

  connectedCallback() {
    super.connectedCallback();
    this.#config = this.#parseConfig();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateInventory);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateInventory);
  }

  /**
   * @returns {InventoryConfig | null}
   */
  #parseConfig() {
    const script = this.querySelector('script[data-inventory-config]');
    if (!script?.textContent) return null;
    try {
      return /** @type {InventoryConfig} */ (JSON.parse(script.textContent));
    } catch {
      return null;
    }
  }

  /**
   * Updates the inventory.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateInventory = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    if (event.detail.data.clientSide) {
      this.#updateInventoryClientSide(event);
      return;
    }

    const newInventory = event.detail.data.html.querySelector('product-inventory');

    if (!newInventory) return;

    morph(this, newInventory, { childrenOnly: true });
  };

  /**
   * Recomputes the inventory status/text for the selected variant without a server request.
   * Mirrors the logic in blocks/product-inventory.liquid.
   * @param {VariantUpdateEvent} event
   */
  #updateInventoryClientSide(event) {
    if (!this.#config) return;

    const status = this.querySelector('.product-inventory__status');
    const icon = this.querySelector('.product-inventory__icon');
    const text = this.querySelector('.product-inventory__text');
    if (!(status instanceof HTMLElement) || !(icon instanceof HTMLElement) || !(text instanceof HTMLElement)) return;

    const variant = /** @type {{ id?: string | number } | null} */ (event.detail.resource);

    // No matching variant (unavailable combination) — hide the inventory line.
    if (!variant || variant.id == null) {
      status.hidden = true;
      return;
    }

    status.hidden = false;

    const info = this.#config.variants[String(variant.id)];
    const { state, label } = this.#resolveStatus(info);

    icon.className = `svg-wrapper product-inventory__icon product-inventory__icon-${state}`;
    text.textContent = label;
  }

  /**
   * @param {InventoryVariantInfo | undefined} info
   * @returns {{ state: 'low' | 'in_stock' | 'out_of_stock', label: string }}
   */
  #resolveStatus(info) {
    const { threshold, showQuantity, labels } = /** @type {InventoryConfig} */ (this.#config);

    if (!info || !info.managed) {
      return { state: 'in_stock', label: labels.inStock };
    }

    if (info.quantity > 0) {
      if (info.quantity <= threshold) {
        const label = showQuantity
          ? labels.lowStockShowCount.replace('__COUNT__', String(info.quantity))
          : labels.lowStock;
        return { state: 'low', label };
      }
      return { state: 'in_stock', label: labels.inStock };
    }

    if (info.policy === 'continue') {
      return { state: 'in_stock', label: labels.inStock };
    }

    return { state: 'out_of_stock', label: labels.outOfStock };
  }
}

if (!customElements.get('product-inventory')) {
  customElements.define('product-inventory', ProductInventory);
}
